import { Currency } from "../../../constants";
import { useRateAge } from "../../../hooks/useRateAge";

interface ExchangeRateDisplayProps {
  baseCurrency: Currency;
  quoteCurrency: Currency;
  rate: number;
  fetchedAt: Date;
  isRefreshing: boolean;
  onRefresh: () => void;
}

export function ExchangeRateDisplay({
  baseCurrency,
  quoteCurrency,
  rate,
  fetchedAt,
  isRefreshing,
  onRefresh,
}: ExchangeRateDisplayProps) {
  const { ageSeconds, status } = useRateAge(fetchedAt);

  const getStatusColor = () => {
    switch (status) {
      case "fresh":
        return "text-green-600";
      case "aging":
        return "text-yellow-600";
      case "stale":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusDotColor = () => {
    switch (status) {
      case "fresh":
        return "bg-green-500";
      case "aging":
        return "bg-yellow-500";
      case "stale":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatAge = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s ago`;
    }
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  return (
    <div className="exchange-rate-display bg-gray-50 rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Exchange Rate</span>
          <div
            className={`w-2 h-2 rounded-full ${getStatusDotColor()} ${
              status === "fresh" ? "animate-pulse" : ""
            }`}
          />
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="p-1 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50"
          aria-label="Refresh rate"
        >
          <svg
            className={`w-4 h-4 text-gray-600 ${
              isRefreshing ? "animate-spin" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      <div className="flex items-baseline justify-between">
        <p className="text-lg font-bold text-gray-900">
          1 {baseCurrency} = {rate.toFixed(4)} {quoteCurrency}
        </p>
        <p className={`text-xs ${getStatusColor()} font-medium`}>
          Updated {formatAge(ageSeconds)}
        </p>
      </div>

      {status === "stale" && (
        <div className="bg-red-50 border border-red-200 rounded p-2 mt-2">
          <p className="text-xs text-red-700">
            Rate is outdated. Please refresh for the current rate.
          </p>
        </div>
      )}
    </div>
  );
}
